import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Container from "@/components/Container";
import useMediaQuery from "@/custom_hooks/mediaQuery";
import MediaList from "@/components/Media/MediaList";
import { Button } from "@/components/UI/Buttons/Button";
import { BLOG_CONTENT_VALUES } from "@/components/Blog/BlogsData";
import { BLOG_TAGS } from "@/components/Blog/BlogTags";
import FAQSection from "@/components/FAQSection";
import { COLLAPSABLE_CONTENT_HOME_FAQ_VALUES } from "@/components/Collapsible/constants";
import BlogCard from "@/components/Blog/BlogCard";
import { ActionLink } from "@/components/UI/Buttons";

const BlogList: FC = () => {
  const isMobile = useMediaQuery(768);

  const [visibleBlogsCount, setVisibleBlogsCount] = useState(4);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const filteredBlogs = useMemo(
    () =>
      [
        ...(selectedTag && selectedTag !== "Visi"
          ? BLOG_CONTENT_VALUES.filter((blog) =>
              blog.tags?.includes(selectedTag),
            )
          : BLOG_CONTENT_VALUES),
      ].reverse(),
    [selectedTag],
  );

  useEffect(() => {
    if (isMobile === true) {
      setVisibleBlogsCount(3);
    } else {
      setVisibleBlogsCount(13); // 1 featured + 6 grid (2 rows of 3)
    }
  }, [isMobile]);

  const handleLoadMore = () => {
    setVisibleBlogsCount((prevCount) => prevCount + (isMobile ? 3 : 3));
  };

  const handleTagClick = (tag: string) => {
    const isSameTag = tag === selectedTag;
    setSelectedTag(isSameTag ? null : tag);
    setVisibleBlogsCount(isMobile ? 3 : 13); // Reset count
  };

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      const handleWheel = (e: WheelEvent) => {
        if (e.deltaY !== 0) {
          e.preventDefault();
          scrollContainer.scrollLeft += e.deltaY;
          checkScroll();
        }
      };
      scrollContainer.addEventListener("wheel", handleWheel, {
        passive: false,
      });
      return () => scrollContainer.removeEventListener("wheel", handleWheel);
    }
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [filteredBlogs]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -250 : 250;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(checkScroll, 400); // Check after animation
    }
  };

  const visibleBlogs = filteredBlogs.slice(0, visibleBlogsCount);

  // Safety check just in case visibleBlogs is empty
  const featuredBlog = visibleBlogs.length > 0 ? visibleBlogs[0] : null;
  const gridBlogs = visibleBlogs.length > 1 ? visibleBlogs.slice(1) : [];

  return (
    <div className="md:py-section flex flex-col bg-white">
      {/* Label Section */}
      <section className="flex flex-col px-4 pt-25 pb-6 md:px-[60px] md:pt-25 md:pb-7.5">
        <Container>
          <div className="mx-auto mb-6 flex w-full max-w-[960px] flex-col items-center justify-center gap-7.5 text-center font-['Figtree:Regular',sans-serif] md:mb-7.5">
            <h1 className="text-dark text-[34px] leading-[1.2] font-light md:text-[44px]">
              Telomera tinklaraštis – genetika,{" "}
              <br className="hidden md:block" />
              sveikata ir ilgaamžiškumas
            </h1>
            <p className="text-muted max-w-[580px] text-base leading-[1.5] font-light">
              Naujausi atradimai, įžvalgos ir patarimai, kaip genetika gali
              padėti gyventi sveikiau.
            </p>
          </div>

          {/* Tags */}
          <div className="relative mt-6 w-full md:mt-0">
            {/* Left Arrow & Fade */}
            <div
              className={`pointer-events-none absolute inset-y-0 left-0 z-10 flex w-20 items-center justify-start bg-gradient-to-r from-transparent to-transparent transition-opacity duration-300 md:w-32 ${showLeftArrow ? "opacity-100" : "opacity-0"}`}
              style={{
                background: showLeftArrow
                  ? "linear-gradient(to right, white 50%, transparent)"
                  : "none",
              }}
            >
              <button
                onClick={() => scroll("left")}
                className="bg-surface pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-transform hover:scale-110 md:h-12 md:w-12"
                aria-label="Slenkti į kairę"
              >
                <ChevronLeft className="text-dark h-6 w-6" />
              </button>
            </div>

            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="scrollbar-hide mx-auto w-max max-w-full overflow-x-auto overflow-y-hidden scroll-smooth pb-2 md:pb-0"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <div className="flex w-max items-center justify-start gap-[10px] px-4 whitespace-nowrap md:px-0">
                <button
                  onClick={() => handleTagClick("Visi")}
                  className={`border-stroke h-[52px] cursor-pointer rounded-[75px] border px-[26px] py-[14px] transition-colors ${!selectedTag || selectedTag === "Visi" ? "bg-dark border-dark text-white" : "text-dark hover:border-dark border-[#ededed] bg-white"}`}
                >
                  Visi
                </button>
                {Object.values(BLOG_TAGS).map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => handleTagClick(label)}
                    className={`border-stroke h-[52px] cursor-pointer rounded-[75px] border px-[26px] py-[14px] transition-colors ${selectedTag === label ? "bg-dark border-dark text-white" : "text-dark hover:border-dark border-[#ededed] bg-white"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right Arrow & Fade */}
            <div
              className={`pointer-events-none absolute inset-y-0 right-0 z-10 flex w-20 items-center justify-end bg-gradient-to-l from-transparent to-transparent transition-opacity duration-300 md:w-32 ${showRightArrow ? "opacity-100" : "opacity-0"}`}
              style={{
                background: showRightArrow
                  ? "linear-gradient(to left, white 50%, transparent)"
                  : "none",
              }}
            >
              <button
                onClick={() => scroll("right")}
                className="bg-surface pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full shadow-sm transition-transform hover:scale-110 md:h-12 md:w-12"
                aria-label="Slenkti į dešinę"
              >
                <ChevronRight className="text-dark h-6 w-6" />
              </button>
            </div>

            <style>
              {`
                .scrollbar-hide::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
          </div>
        </Container>
      </section>

      {/* Featured Blog */}
      {!isMobile && featuredBlog && (
        <section className="flex flex-col px-4 pb-6 md:px-[60px] md:pb-[40px]">
          <Container>
            <div className="border-grey-300 flex w-full flex-col items-stretch gap-10 overflow-hidden rounded-[20px] border md:h-[450px] md:flex-row">
              <div className="bg-grey-100 relative h-[240px] w-full shrink-0 md:h-full md:w-1/2">
                <img
                  src={featuredBlog.img_src}
                  alt={featuredBlog.label}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-grow flex-col justify-between py-[30px] pr-[30px] pb-[30px] pl-[30px] md:pl-0">
                <div className="flex flex-col gap-[12px]">
                  <div className="flex flex-col gap-2">
                    {featuredBlog.tags && featuredBlog.tags.length > 0 && (
                      <div className="bg-grey-100 mb-1 w-fit rounded-[28px] px-[14px] py-1">
                        <span className="text-grey-500 text-xs font-medium tracking-wider uppercase">
                          {featuredBlog.tags[0]}
                        </span>
                      </div>
                    )}
                    <h2 className="text-dark text-[34px] leading-[1.2] font-normal">
                      {featuredBlog.label}
                    </h2>
                  </div>
                  <p className="text-muted mt-2 line-clamp-4 text-base leading-[1.5]">
                    {featuredBlog.descriptionInner || featuredBlog.description}
                  </p>
                </div>
                <div className="mt-8 md:mt-0">
                  <ActionLink
                    to={`/tinklarastis/${featuredBlog.slug}`}
                    className="inline-flex w-40"
                    variant="dark"
                    hoverGradientLight
                    onHover
                    showIcon={false}
                  >
                    Skaityti
                  </ActionLink>
                </div>
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Mobile Featured works like a card but is the first in the list */}
      {isMobile && featuredBlog && (
        <section className="flex flex-col px-4 pb-6">
          <Container>
            <BlogCard
              slug={featuredBlog.slug}
              label={featuredBlog.label}
              description={
                featuredBlog.descriptionInner || featuredBlog.description
              }
              img_src={featuredBlog.img_src}
              tags={featuredBlog.tags}
              date={featuredBlog.date}
            />
          </Container>
        </section>
      )}

      {/* Blog Grid */}
      {gridBlogs.length > 0 && (
        <section className="flex flex-col px-4 pb-6 md:px-[60px] md:pb-[100px]">
          <Container>
            <div className="grid w-full gap-[20px] gap-y-[40px] md:grid-cols-3">
              {gridBlogs.map((blog) => (
                <BlogCard
                  key={blog.id}
                  slug={blog.slug}
                  label={blog.label}
                  description={blog.descriptionInner || blog.description}
                  img_src={blog.img_src}
                  tags={blog.tags}
                  date={blog.date}
                />
              ))}
            </div>
            {visibleBlogsCount < filteredBlogs.length && (
              <div className="mt-10 flex w-full justify-center md:mt-16">
                <Button
                  onClick={handleLoadMore}
                  className="h-[63px] w-auto rounded-[75px] border border-[#ededed] px-[26px] py-[20px]"
                  variant="white"
                  onHover
                  hoverDark
                >
                  Žiūrėti daugiau
                </Button>
              </div>
            )}
          </Container>
        </section>
      )}

      {/* Media Section */}
      <section className="bg-surface px-4 py-15 md:px-[60px] md:py-25">
        <Container>
          <div className="mb-10 flex w-full flex-col items-center justify-center gap-5 text-center font-['Figtree:Regular',sans-serif]">
            <h2 className="text-dark text-[34px] leading-[1.2] font-light">
              Telomera žiniasklaidoje
            </h2>
            <p className="text-muted text-base font-light">
              Ekspertų įžvalgos ir straipsniai.
            </p>
          </div>
          <MediaList />
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection
        data={COLLAPSABLE_CONTENT_HOME_FAQ_VALUES.items}
        variant="white"
      />
    </div>
  );
};

export default BlogList;
